import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Mail, Eye, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PendingActions() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Loading pending actions…</div>;

  const sendReminder = async (poId, channel) => {
    try {
      await api.post(`/pos/${poId}/reminder`, { channel, template_key: "delivery_update_reminder" });
      toast.success("Reminder logged successfully");
      load();
    } catch (e) { toast.error("Could not send reminder"); }
  };

  const red = data.attention.filter((a) => a.severity === "red");
  const amber = data.attention.filter((a) => a.severity !== "red");

  const Group = ({ title, items, tone }) => (
    <div className="rounded-lg border bg-card">
      <h2 className={`border-b px-5 py-3 font-heading text-base font-semibold ${tone}`}>{title} <span className="ml-1 font-mono text-sm">{items.length}</span></h2>
      <div className="divide-y">
        {items.length === 0 && <p className="p-5 text-sm text-muted-foreground">Nothing here.</p>}
        {items.map((a, i) => (
          <div key={`${a.po_id}-${a.issue}`} data-testid={`pending-${tone.includes("red") ? "red" : "amber"}-${i}`} className="flex flex-wrap items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50">
            <AlertCircle className={`h-5 w-5 shrink-0 ${a.severity === "red" ? "text-[#EF4444]" : "text-[#F59E0B]"}`} />
            <div className="min-w-0 flex-1">
              <button data-testid={`pending-po-${a.po_number}`} onClick={() => navigate(`/buyer/pos/${a.po_id}`)} className="font-mono text-sm font-semibold hover:underline">{a.po_number}</button>
              <p className="text-sm text-muted-foreground">Supplier: {a.supplier_name}</p>
              <p className="text-sm"><span className="font-medium">Issue:</span> {a.issue}{a.age_days > 0 ? <span className="text-muted-foreground"> · pending for {a.age_days} day{a.age_days === 1 ? "" : "s"}</span> : null}</p>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" data-testid={`pending-wa-${i}`} onClick={() => sendReminder(a.po_id, "whatsapp")}><MessageCircle className="mr-1 h-3.5 w-3.5 text-green-700" /> WhatsApp Reminder</Button>
              <Button size="sm" variant="outline" data-testid={`pending-email-${i}`} onClick={() => sendReminder(a.po_id, "email")}><Mail className="mr-1 h-3.5 w-3.5 text-blue-700" /> Email Reminder</Button>
              <Button size="sm" data-testid={`pending-view-${i}`} onClick={() => navigate(`/buyer/pos/${a.po_id}`)} className="bg-slate-900 hover:bg-slate-800"><Eye className="mr-1 h-3.5 w-3.5" /> {a.cta === "Review Update" ? "Review Request" : "View PO"}</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="pending-actions-page" className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Pending Actions</h1>
        <p className="text-sm text-muted-foreground">Every PO waiting on you or a supplier — act on it directly from here.</p>
      </div>
      <Group title="Urgent — overdue / delayed" items={red} tone="text-[#991B1B]" />
      <Group title="Attention — pending acknowledgements & requests" items={amber} tone="text-[#92400E]" />
    </div>
  );
}
